import { useMutation } from '@apollo/react-hooks';
import moment from 'moment';
import React, { useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import Button from '../../Button';
import * as Price from '../../../util/Price';
import * as Phone from '../../../util/PhoneNumber';
import { RESET_ORG, SET_MMS_ENABLED, SET_ORG_CONTEXT_ID } from '../../../graphql/queries';
import colors from '../../../styles/colors';
import BaseModal, { ModalList } from '../../modals/BaseModal';

const ListWrapper = styled.ul`
  width: clamp(250px, 75vw, 300px);
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.05);
  margin: 0 10px;
  list-style: none;
  padding: 0 15px;
  overflow: auto;
  position: relative;
`;

const ListHeader = styled.h2`
  min-height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-bottom: 1px solid ${colors.grey4};
  margin-bottom: 5x;
`;

const ListItem = styled.li`
  min-height: 30px;
  text-align: justify;
  border-bottom: 1px solid ${colors.grey5};
  padding: 10px;
  margin-bottom: 5x;
  font-size: 13px;

  display: flex;
  flex-direction: row;
`;

const ListKey = styled.div`
  min-width: 100px;
  font-weight: 450;
  display: flex;
  align-items: center;
`;

const ListValue = styled.div`
  display: flex;
  flex: 1;
  color: ${colors.grey2};
  padding-left: 10px;
`;

const ButtonContainer = styled.div`
  padding: 20px 0px;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  border-radius: 7px;
  min-height: 40px;
  position: sticky;
  bottom: 0;
  flex-direction: column;
  background: ${colors.white};
`;

const ClientDetailsModal = ({
  clientData: clientInfo,
  refetch,
  mmsActivatedIds,
  setMmsActivatedIds,
  isStagingEnv,
}) => {
  const [mmsEnabledModal, setMmsEnabledModal] = useState(false);
  const [resetOrgModal, setResetOrgModal] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [clientData, setClientData] = useState(clientInfo);
  const [setOrganizationContextId, { loading }] = useMutation(
    SET_ORG_CONTEXT_ID,
    {
      variables: {
        organizationId: clientData._id,
      },
      onError(error) {
        console.log(error);
      },
      onCompleted(data) {
        localStorage.setItem(
          'CHECK_THIS_OUT_AUTH_TOKEN',
          data.setOrganizationContextId.token,
        );
        window.location.replace('/admin/redeemCoupon');
      },
    },
  );

  const [sendMmsEnabled, { loading: mmsLoading }] = useMutation(
    SET_MMS_ENABLED,
    {
      variables: {
        organizationId,
        mmsEnabled: 'Y',
      },

      onError(error) {
        console.log(error);
      },
      onCompleted() {
        refetch();
        setMmsActivatedIds((old) => [...old, organizationId]);
        setMmsEnabledModal(false);
      },
    },
  );

  const [resetOrganization, { loading: resetOrgLoading }] = useMutation(
    RESET_ORG,
    {
      variables: {
        organizationId,
      },

      onError(error) {
        console.log(error);
      },
      onCompleted(response) {
        const updatedData = response?.resetOrganization;
        if (updatedData) setClientData(updatedData);
        refetch();
        toast.success('Organization has been reset successfully');
        setResetOrgModal(false);
      },
    },
  );

  return (
    <>
      <BaseModal
        open={mmsEnabledModal}
        onClose={setMmsEnabledModal}
        modalType={ModalList.SetMmsEnabledModal}
        header={`Are you sure you want to set ${orgName} to MMS Enabled?`}
        onConfirm={sendMmsEnabled}
        loading={mmsLoading}
      />
      {isStagingEnv && (
        <BaseModal
          open={resetOrgModal}
          onClose={setResetOrgModal}
          modalType={ModalList.ResetOrgModal}
          noDelete
          header={`Are you sure you want to clear all data of ${orgName}?`}
          onConfirm={resetOrganization}
          loading={resetOrgLoading}
        />
      )}
      <ListWrapper>
        {clientData && (
          <>
            <ListHeader>{clientData.name}</ListHeader>
            <ListItem>
              <ListKey>Organization Id :</ListKey>{' '}
              <ListValue> {clientData._id}</ListValue>
            </ListItem>
            <ListItem>
              <ListKey>Subscription Period :</ListKey>{' '}
              <ListValue>
                {' '}
                {`${moment(clientData.cycleStartAt * 1000).format(
                  'MM/D/YYYY',
                )} - ${moment(clientData.cycleEndAt * 1000).format(
                  'MM/D/YYYY',
                )}`}
              </ListValue>
            </ListItem>
            <ListItem>
              <ListKey>LTV :</ListKey>
              <ListValue> {`$${Price.output(clientData.ltv, true)}`}</ListValue>
            </ListItem>
            <ListItem>
              <ListKey>Upcoming Bill :</ListKey>
              <ListValue>
                {`$${Price.output(clientData.upcomingRevenue, true)}`}
              </ListValue>
            </ListItem>
            <ListItem>
              <ListKey>Date Joined :</ListKey>
              <ListValue>
                {moment(clientData.createdAt * 1000).format('MMM Do, YYYY')}
              </ListValue>
            </ListItem>
            <ListItem>
              <ListKey>Phone Number :</ListKey>
              <ListValue>{clientData.phoneNumber ? Phone.formatPhoneNumber(clientData.phoneNumber) : ''}</ListValue>
            </ListItem>
            <ListItem>
              <ListKey># Active Contacts :</ListKey>
              <ListValue>{clientData.totalContacts ? clientData.totalContacts.toLocaleString() : '0'}</ListValue>
            </ListItem>
            <ListItem>
              <ListKey>SMS/MMS Campaigns :</ListKey>
              <ListValue>{clientData.totalCampaigns || '0'}</ListValue>
            </ListItem>
            <ListItem>
              <ListKey>Credits Used :</ListKey>
              <ListValue>{clientData.campaignCreditsUsed ? clientData.campaignCreditsUsed.toLocaleString() : '0'}</ListValue>
            </ListItem>

            <ListItem>
              <ListKey>MMS Enabled :</ListKey>
              <ListValue>
                {clientData.mmsEnabled === 'Y'
                || mmsActivatedIds.includes(clientData._id) ? (
                    'Yes'
                  ) : (
                    <>
                      {!!clientData.campaignPhoneNumber && (
                        <Button
                          text="Set Enabled"
                          type="button"
                          onClick={() => {
                            setOrgName(clientData.name);
                            setOrganizationId(clientData._id);
                            setMmsEnabledModal(true);
                          }}
                          width="100%"
                          height="25px"
                          padding="20px 5px"
                        />
                      )}
                    </>
                  )}
              </ListValue>
            </ListItem>

            <ButtonContainer>
              {isStagingEnv && (
                <Button
                  text="Reset"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOrgName(clientData.name);
                    setOrganizationId(clientData._id);
                    setResetOrgModal(true);
                  }}
                  width="50%"
                  xswidth="90%"
                  height="30px"
                  loading={resetOrgLoading}
                />
              )}

              <Button
                text="Impersonate"
                onClick={() => setOrganizationContextId()}
                width="50%"
                xswidth="90%"
                loading={loading}
                type="button"
                padding="10px 0"
                height="30px"
                margin="5px 0"
              />
            </ButtonContainer>
          </>
        )}
      </ListWrapper>
    </>
  );
};

export default ClientDetailsModal;
